import React, { useEffect, memo } from 'react'
import { GetDataError } from 'restful-react'
import { Button } from 'transfix-ui/components/Button'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box } from 'transfix-ui/core'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useDownloadRFPMatchingCarriers } from 'apps/marketplace/apiTypes/requestForProposalMatchingCarriers'
import { blobDownload } from 'apps/marketplace/utils/api/apiFileDownload'
import { ShipperRateObjectAugmented } from 'apps/marketplace/utils/hooks/AwardedLanes/useAwardedLanesApi'
import { useRequestCallListApi } from 'apps/marketplace/utils/hooks/useRequestCallList'

interface IAwardedLaneModalActionsProps {
  currentRate: ShipperRateObjectAugmented
}

export const AwardedLaneModalActions = memo<IAwardedLaneModalActionsProps>(({ currentRate }) => {
  const { openToast } = useGlobalToast()
  const { getFlag } = useFeatureFlagContext()

  const { enabled: showRfpLaneMatchDownload } = getFlag('rfpLaneMatchDownload')
  const {
    data: rfpMatchingCarriersData,
    error: rfpMatchingCarriersError,
    refetch: downloadMatches,
  } = useDownloadRFPMatchingCarriers({
    lazy: true,
  })
  const { error: callListApiError, requestCallList } = useRequestCallListApi()

  // Opens the error toast if there's an error
  useEffect(() => {
    if (callListApiError) {
      openToast({
        toastMessage: callListApiError,
        toastType: 'alert',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callListApiError])

  // Once a request is made to grab RFP Matching Carriers, we need to convert the response to a file that can be downloaded
  // A blob is converted into a temporary URL that is then "clicked" to trigger the download
  useEffect(() => {
    if (rfpMatchingCarriersData) {
      const { rfpId, rfpLaneId } = currentRate || {}
      const fileName = `${rfpId}_${rfpLaneId}_${Date.now()}.csv`
      ;(rfpMatchingCarriersData as Response).blob().then(blob => blobDownload(blob, fileName))
    }
  }, [currentRate, rfpMatchingCarriersData])

  // If an error occured while retrieving a list of RFP Matching Carriers, show the error in a toast
  useEffect(() => {
    if (rfpMatchingCarriersError) {
      const error = rfpMatchingCarriersError as GetDataError<{ message: string }>
      const message = typeof error?.data === 'string' ? error?.data : error?.data?.message
      openToast({
        toastMessage: message,
        toastType: 'alert',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rfpMatchingCarriersError])

  const requestRfpMatchingCarriers = () => {
    downloadMatches({
      queryParams: {
        filters: {
          rfpId: currentRate.rfpId,
          rfpLaneId: currentRate.rfpLaneId,
        },
      },
    })
  }

  const requestCsv = async () => {
    const { city: originCity, stateCode: originStateCode } = currentRate.pickup || {}
    const { city: destinationCity, stateCode: destinationStateCode } = currentRate.delivery || {}
    if (originCity && originStateCode && destinationCity && destinationStateCode) {
      const requestBody = {
        origin: {
          city: originCity,
          stateCode: originStateCode,
          radiusMiles: 50,
        },
        destination: {
          city: destinationCity,
          stateCode: destinationStateCode,
          radiusMiles: 50,
        },
        source: 'routing_guide',
        shipperApplicationId: '',
        equipmentId: `${currentRate.equipment?.parentId}` || '3',
      }
      const response = await requestCallList(requestBody)
      if (response?.message) {
        openToast({
          toastMessage: "You'll get a CSV via Slack shortly.",
          toastType: 'default',
        })
      }
    }
  }

  return (
    <Box textAlign='right' display='flex' justifyContent='flex-end'>
      {showRfpLaneMatchDownload && (
        <Button
          onClick={requestRfpMatchingCarriers}
          color='secondary'
          size='small'
          id='get-call-list'
          data-test='get-call-list'>
          Get Lane List Matches
        </Button>
      )}
      <Button
        onClick={requestCsv}
        size='small'
        color='secondary'
        id='get-call-list'
        data-test='get-call-list'
        className='ml1'>
        Get Call List Matches
      </Button>
    </Box>
  )
})
